import styled from "styled-components";

export const Layout = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  background: #fff;
  font-family: Inter, sans-serif;
`;

export const ScrollRegion = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 24px 24px 32px;

  @media (max-width: 600px) {
    padding: 20px 16px;
  }
`;

export const LedgerHeader = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  padding-bottom: 16px;
  border-bottom: 1px solid var(--PEARL, #ecf1f4);

  @media (max-width: 600px) {
    align-items: flex-start;
    flex-wrap: wrap;
  }
`;

export const LedgerTitle = styled.div`
  color: var(--ASH, #676879);
  font:
    600 12px/1.4 Inter,
    sans-serif;
  letter-spacing: 0.03em;
  text-transform: uppercase;

  strong {
    display: block;
    color: var(--DARK-GREY, #0e0e2c);
    font:
      600 16px/1.4 Inter,
      sans-serif;
    letter-spacing: 0;
    text-transform: none;
  }
`;

export const Filters = styled.div`
  display: inline-flex;
  padding: 2px;
  border-radius: 4px;
  border: 1px solid var(--PEARL, #ecf1f4);
  background: var(--MIST, #f7f8f9);

  button {
    min-width: 52px;
    min-height: 32px;
    border: 0;
    border-radius: 4px;
    padding: 8px 12px;
    background: transparent;
    color: var(--GREY, #4a4a68);
    font:
      600 12px/1 Inter,
      sans-serif;
    text-transform: uppercase;
    cursor: pointer;

    &:focus-visible {
      outline: 2px solid var(--BLUE, #2060ea);
      outline-offset: 2px;
    }
  }

  button[aria-pressed="true"] {
    background: var(--BLUE, #2060ea);
    color: #fff;
  }

  @media (max-width: 600px) {
    width: 100%;

    button {
      flex: 1;
    }
  }
`;

export const EmptyState = styled.div`
  text-align: center;
  padding: 42px 16px;
  color: var(--ASH, #676879);
  font-size: 14px;
`;

export const Hint = styled.p`
  margin: 0;
  color: var(--ASH, #676879);
  font:
    400 12px/1.5 Inter,
    sans-serif;

  code {
    border-radius: 3px;
    padding: 2px 4px;
    background: var(--MIST, #f7f8f9);
    color: var(--DARK-BLUE, #1249bf);
    font:
      600 12px/1.5 Inter,
      sans-serif;
  }
`;

export const ActivityFooter = styled(Hint)`
  margin-top: 24px;
  text-align: center;
`;

export const ComposerShell = styled.section`
  flex-shrink: 0;
  border-bottom: 1px solid var(--PEARL, #ecf1f4);
  background: var(--CLOUD, #fcfdfe);
  padding: 16px 24px 20px;
  box-shadow: 0 8px 28px rgba(23, 30, 45, 0.035);

  @media (max-width: 600px) {
    padding: 14px 16px 18px;
  }
`;

export const ComposerTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 12px;
`;

export const FieldLabel = styled.label`
  display: block;
  color: var(--DARK-GREY, #0e0e2c);
  font:
    600 12px/1.3 Inter,
    sans-serif;
  text-transform: uppercase;
`;

export const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const ToggleLabel = styled.span`
  color: var(--GREY, #4a4a68);
  font:
    600 12px/1.3 Inter,
    sans-serif;
  text-transform: uppercase;
`;

export const ComposerCard = styled.div`
  border: 1px solid var(--LIGHT-ASH, #b9b9c6);
  border-radius: 4px;
  background: #fff;
  min-height: 88px;
  padding: 12px;

  &:focus-within {
    border-color: var(--BLUE, #2060ea);
  }
`;

export const TextArea = styled.textarea`
  display: block;
  width: 100%;
  min-height: 44px;
  max-height: 120px;
  resize: vertical;
  border: 0;
  outline: 0;
  padding: 0;
  background: transparent;
  color: var(--GREY, #4a4a68);
  font:
    14px/1.5 Inter,
    sans-serif;

  &::placeholder {
    color: var(--ASH, #676879);
  }
`;

export const ComposerFooter = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  min-height: 32px;
  margin-top: 12px;
`;

export const ComposerActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
`;

export const SaveHint = styled.span`
  color: var(--ASH, #676879);
  font:
    400 12px/1.3 Inter,
    sans-serif;
  white-space: nowrap;
`;

export const TaskOptions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 18px;
  padding: 12px 0 0;

  > div {
    min-width: 180px;
  }
`;

export const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 7px;
`;

export const Chip = styled.button`
  min-height: 32px;
  border: 1px solid ${({ $active }) => ($active ? "var(--BLUE, #2060ea)" : "var(--PEARL, #ecf1f4)")};
  border-radius: 4px;
  padding: 8px 12px;
  background: ${({ $active }) => ($active ? "var(--BLUE, #2060ea)" : "var(--MIST, #f7f8f9)")};
  color: ${({ $active }) => ($active ? "#fff" : "var(--GREY, #4a4a68)")};
  font:
    600 12px/1 Inter,
    sans-serif;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid var(--BLUE, #2060ea);
    outline-offset: 2px;
  }
`;

export const UndoBar = styled.p`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin: 0 0 12px;
  padding: 10px 12px;
  border-radius: 4px;
  background: var(--MIST, #f7f8f9);
  color: var(--GREY, #4a4a68);
  font:
    500 13px/1.4 Inter,
    sans-serif;

  button {
    border: 0;
    background: transparent;
    color: var(--DARK-BLUE, #1249bf);
    font:
      600 13px/1 Inter,
      sans-serif;
    cursor: pointer;
  }
`;
